import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Award, ArrowLeft, Search, ExternalLink,
  Calendar, DollarSign, Building2, MapPin, FileText, Download,
  AlertCircle, Lock, Crown, Loader2, X, Clock,
  Target, TrendingUp, Sparkles, Info
} from 'lucide-react';

const STATES = [
  { code: 'QLD', name: 'Queensland' },
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'SA', name: 'South Australia' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'NT', name: 'Northern Territory' },
  { code: 'ACT', name: 'Australian Capital Territory' }
];

const SECTORS = [
  'All',
  'Construction & Infrastructure',
  'Professional Services',
  'Manufacturing',
  'Retail & Hospitality',
  'Transport & Logistics',
  'Healthcare',
  'Education',
  'Agriculture',
  'Technology',
  'Industrial/Carbon'
];

const GrantsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [grants, setGrants] = useState([]);
  const [filteredGrants, setFilteredGrants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedSector, setSelectedSector] = useState('All');
  const [showAllIndustries, setShowAllIndustries] = useState(false);
  const { currentOrg } = useOutletContext();

  // Keep track of active fetching so we don't spam


  // Calculate match score based on org profile
  const calculateMatchScore = useCallback((grant, org) => {
    if (!org) return { score: 0, reasons: [] };

    let score = 0;
    const reasons = [];

    // Region match (30 points)
    if (grant.region === 'National') {
      score += 30;
      reasons.push('Available nationally');
    } else if (grant.region === org.state) {
      score += 30;
      reasons.push(`Matches your state (${org.state})`);
    }

    // Sector match (40 points)
    if (grant.sector === 'All') {
      score += 40;
      reasons.push('Open to all industries');
    } else if (grant.sector === org.sector) {
      score += 40;
      reasons.push(`Matches your sector (${org.sector})`);
    }

    // Active/Rolling bonus (20 points)
    if (grant.is_active) {
      score += 10;
      reasons.push('Currently accepting applications');
    }
    if (grant.is_rolling) {
      score += 10;
      reasons.push('Rolling application - no deadline');
    }

    // Urgency bonus (10 points)
    if (grant.days_until_close && grant.days_until_close <= 30) {
      score += 10;
      reasons.push('Closing soon - act fast!');
    }

    return { score: Math.min(score, 100), reasons };
  }, []);

  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exportingGrant, setExportingGrant] = useState(null);
  const [useMatchScores, setUseMatchScores] = useState(true);
  const [showMatchInfo, setShowMatchInfo] = useState(null);

  // Fetch grants - direct Supabase query
  const fetchOrgAndGrants = useCallback(async () => {
    if (!currentOrg) return;

    setLoading(true);
    try {
      const overrideTier = currentOrg.subscription_override?.replace('manual_', '');
      setIsPro(currentOrg.subscription_status === 'pro' || overrideTier === 'pro');

      // Set default state from profile
      if (currentOrg.state) {
        setSelectedState(currentOrg.state);
      }
      // Set default sector from profile
      if (currentOrg.sector) {
        setSelectedSector(currentOrg.sector);
      }

      // Fetch all active grants
      const { data: grantsData, error } = await supabase
        .from('grants')
        .select('*')
        .eq('is_active', true)
        .order('closes_date', { ascending: true, nullsLast: true });

      if (error) throw error;

      // Process grants with calculated fields
      const now = new Date();
      const processedGrants = (grantsData || []).map(grant => {
        // Calculate days until close
        let daysUntilClose = null;
        let urgencyTag = null;

        if (grant.closes_date && !grant.is_rolling) {
          const closeDate = new Date(grant.closes_date);
          daysUntilClose = Math.ceil((closeDate - now) / (1000 * 60 * 60 * 24));

          if (daysUntilClose <= 7) {
            urgencyTag = 'expiring';
          } else if (daysUntilClose <= 30) {
            urgencyTag = 'closing_soon';
          }
        }

        // Calculate match score
        const { score, reasons } = calculateMatchScore(grant, currentOrg);

        return {
          ...grant,
          days_until_close: daysUntilClose,
          urgency_tag: urgencyTag,
          match_score: score,
          match_reasons: reasons
        };
      });

      // Sort by match score (descending)
      processedGrants.sort((a, b) => (b.match_score || 0) - (a.match_score || 0));

      setGrants(processedGrants);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.message === 'Authentication required') {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [currentOrg, calculateMatchScore, navigate]);

  useEffect(() => {
    fetchOrgAndGrants();
  }, [fetchOrgAndGrants]);

  useEffect(() => {
    filterGrants();
  }, [grants, searchTerm, selectedState, selectedSector, showAllIndustries]);

  const filterGrants = () => {
    let filtered = [...grants];

    // Filter by state (show user's state + National)
    if (selectedState) {
      filtered = filtered.filter(g =>
        g.region === selectedState || g.region === 'National'
      );
    }

    // Filter by sector (always show 'All' sector grants)
    if (!showAllIndustries && selectedSector && selectedSector !== 'All') {
      filtered = filtered.filter(g =>
        g.sector === 'All' || g.sector === selectedSector
      );
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(g =>
        g.name.toLowerCase().includes(term) ||
        g.provider.toLowerCase().includes(term) ||
        (g.description && g.description.toLowerCase().includes(term))
      );
    }

    setFilteredGrants(filtered);
  };

  const handleExportAcquittal = async (grant) => {
    if (!isPro) {
      toast.error('Upgrade to Pro to export grant acquittal data');
      return;
    }

    setExportingGrant(grant.id);
    try {
      // Fetch emissions data for the org
      const { data: emissions } = await supabase
        .from('emissions_data')
        .select('*')
        .eq('organisation_id', currentOrg.id);

      // Calculate totals
      const totals = {
        kwh: 0,
        litres: 0,
        tonnes_co2e: 0
      };

      (emissions || []).forEach(e => {
        if (e.unit === 'kWh') totals.kwh += e.quantity || 0;
        if (e.unit === 'L' || e.unit === 'litres') totals.litres += e.quantity || 0;
        totals.tonnes_co2e += e.co2_equivalent || 0;
      });

      // Fetch evidence documents
      const { data: documents } = await supabase
        .from('documents')
        .select('file_name, file_size, file_type')
        .eq('organisation_id', currentOrg.id);

      const exportData = {
        grant_name: grant.name,
        grant_provider: grant.provider,
        exported_at: new Date().toISOString(),
        organisation: {
          name: currentOrg.name,
          abn: currentOrg.abn,
          state: currentOrg.state,
          sector: currentOrg.sector
        },
        emission_totals_fy2026: {
          electricity_kwh: totals.kwh.toFixed(2),
          fuel_litres: totals.litres.toFixed(2),
          total_tonnes_co2e: totals.tonnes_co2e.toFixed(2)
        },
        evidence_documents: (documents || []).map(d => ({
          name: d.file_name,
          size: d.file_size,
          type: d.file_type
        }))
      };

      // Create downloadable JSON
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${grant.name.replace(/\s+/g, '_')}_Acquittal_Data.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Acquittal data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export acquittal data');
    } finally {
      setExportingGrant(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getUrgencyBadge = (grant) => {
    if (grant.is_rolling) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Clock className="w-3 h-3 mr-1" />
          Rolling
        </span>
      );
    }

    if (grant.urgency_tag === 'expiring') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 animate-pulse">
          <AlertCircle className="w-3 h-3 mr-1" />
          Expiring ({grant.days_until_close} days)
        </span>
      );
    }

    if (grant.urgency_tag === 'closing_soon') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          <Clock className="w-3 h-3 mr-1" />
          Closing Soon ({grant.days_until_close} days)
        </span>
      );
    }

    if (grant.closes_date) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
          <Calendar className="w-3 h-3 mr-1" />
          Closes {formatDate(grant.closes_date)}
        </span>
      );
    }

    return null;
  };

  const getMatchScoreBadge = (grant) => {
    if (!useMatchScores || grant.match_score === null || grant.match_score === undefined) {
      return null;
    }

    const score = grant.match_score;
    let bgColor, textColor, icon;

    if (score >= 80) {
      bgColor = 'bg-gradient-to-r from-emerald-500 to-green-500';
      textColor = 'text-white';
      icon = <Sparkles className="w-3 h-3 mr-1" />;
    } else if (score >= 60) {
      bgColor = 'bg-gradient-to-r from-blue-500 to-cyan-500';
      textColor = 'text-white';
      icon = <TrendingUp className="w-3 h-3 mr-1" />;
    } else if (score >= 40) {
      bgColor = 'bg-gradient-to-r from-amber-400 to-yellow-400';
      textColor = 'text-amber-900';
      icon = <Target className="w-3 h-3 mr-1" />;
    } else {
      bgColor = 'bg-slate-200';
      textColor = 'text-slate-600';
      icon = null;
    }

    return (
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMatchInfo(showMatchInfo === grant.id ? null : grant.id);
          }}
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${bgColor} ${textColor} shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
          data-testid={`match-score-${grant.id}`}
        >
          {icon}
          {score}% Match
          <Info className="w-3 h-3 ml-1 opacity-75" />
        </button>

        {/* Match reasons tooltip */}
        {showMatchInfo === grant.id && grant.match_reasons && (
          <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-slate-200 p-3 z-20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-700">Why this matches you:</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMatchInfo(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <ul className="space-y-1.5">
              {grant.match_reasons.map((reason, idx) => (
                <li key={idx} className="flex items-start text-xs text-slate-600">
                  <span className="w-1.5 h-1.5 bg-[#74A57F] rounded-full mr-2 mt-1.5 flex-shrink-0" />
                  {reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Award className="w-7 h-7 mr-3 text-[#74A57F]" />
              National Grant Vault
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Find and apply for matched grants
            </p>
          </div>

          {!isPro && (
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-[#003B5C] text-white hover:bg-[#002A42]"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade for Acquittal Export
              </Button>
            </div>
          )}
        </div>
        {/* Filters Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search grants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="grant-search-input"
              />
            </div>

            {/* State Filter (Mandatory) */}
            <div className="w-full md:w-48">
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger data-testid="state-filter">
                  <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent>
                  {STATES.map(state => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sector Filter */}
            <div className="w-full md:w-56">
              <Select
                value={showAllIndustries ? 'all' : selectedSector}
                onValueChange={(val) => {
                  if (val === 'all') {
                    setShowAllIndustries(true);
                  } else {
                    setShowAllIndustries(false);
                    setSelectedSector(val);
                  }
                }}
              >
                <SelectTrigger data-testid="sector-filter">
                  <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Select Sector" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Show All Industries</SelectItem>
                  {SECTORS.filter(s => s !== 'All').map(sector => (
                    <SelectItem key={sector} value={sector}>
                      {sector}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Active Filters */}
          <div className="flex flex-wrap gap-2 mt-4">
            {selectedState && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#003B5C]/10 text-[#003B5C]">
                <MapPin className="w-3 h-3 mr-1" />
                {STATES.find(s => s.code === selectedState)?.name || selectedState}
                <button
                  onClick={() => setSelectedState('')}
                  className="ml-2 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}

            {!showAllIndustries && selectedSector && selectedSector !== 'All' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#74A57F]/10 text-[#74A57F]">
                <Building2 className="w-3 h-3 mr-1" />
                {selectedSector}
                <button
                  onClick={() => setShowAllIndustries(true)}
                  className="ml-2 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}

            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
              + National Programs (always shown)
            </span>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-slate-600">
            Showing <strong>{filteredGrants.length}</strong> grants
            {selectedState && ` for ${STATES.find(s => s.code === selectedState)?.name || selectedState}`}
          </p>
          {useMatchScores && (
            <div className="flex items-center space-x-2 text-sm">
              <Sparkles className="w-4 h-4 text-emerald-500" />
              <span className="text-slate-600">Sorted by match score</span>
            </div>
          )}
        </div>

        {/* Top Matches Highlight */}
        {useMatchScores && filteredGrants.some(g => g.match_score >= 80) && (
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-4 mb-6 border border-emerald-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-900">
                  {filteredGrants.filter(g => g.match_score >= 80).length} High-Match Grant{filteredGrants.filter(g => g.match_score >= 80).length > 1 ? 's' : ''} Found!
                </h3>
                <p className="text-sm text-emerald-700">
                  These grants closely match your state, sector, and emissions profile.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Grants Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGrants.map(grant => (
            <div
              key={grant.id}
              className={`bg-white rounded-2xl p-6 shadow-sm border transition-all hover:shadow-md ${grant.match_score >= 80 ? 'border-emerald-200 ring-1 ring-emerald-100' :
                grant.match_score >= 60 ? 'border-blue-200' :
                  'border-slate-100'
                }`}
              data-testid={`grant-card-${grant.id}`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${grant.region === 'National'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-purple-100 text-purple-800'
                      }`}>
                      {grant.region}
                    </span>
                    {grant.sector !== 'All' && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                        {grant.sector}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {grant.name}
                  </h3>
                  <p className="text-sm text-slate-500">{grant.provider}</p>
                </div>
                {/* Match Score Badge */}
                {getMatchScoreBadge(grant)}
              </div>

              {/* Urgency Badge */}
              <div className="mb-3">
                {getUrgencyBadge(grant)}
              </div>

              {/* Funding Amount */}
              {grant.funding_amount && (
                <div className="flex items-center space-x-2 mb-3">
                  <DollarSign className="w-4 h-4 text-[#74A57F]" />
                  <span className="text-lg font-bold text-[#74A57F]">{grant.funding_amount}</span>
                </div>
              )}

              {/* Description */}
              <p className="text-sm text-slate-600 mb-4 line-clamp-3">
                {grant.description}
              </p>

              {/* Eligibility */}
              {grant.eligibility_summary && (
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <p className="text-xs font-medium text-slate-500 mb-1">Eligibility:</p>
                  <p className="text-sm text-slate-700 line-clamp-2">{grant.eligibility_summary}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-2">
                {grant.application_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(grant.application_url, '_blank')}
                    className="flex-1"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Apply
                  </Button>
                )}

                <Button
                  variant={isPro ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleExportAcquittal(grant)}
                  disabled={exportingGrant === grant.id}
                  className={isPro ? "flex-1 bg-[#74A57F] hover:bg-[#5F8575]" : "flex-1"}
                  data-testid={`export-acquittal-${grant.id}`}
                >
                  {exportingGrant === grant.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isPro ? (
                    <>
                      <Download className="w-4 h-4 mr-1" />
                      Export Data
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-1" />
                      Pro
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredGrants.length === 0 && (
          <div className="text-center py-12">
            <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">No grants found</h3>
            <p className="text-slate-500">
              Try adjusting your filters or search terms
            </p>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 rounded-2xl p-6 border border-blue-100">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Acquittal Export Tool</h3>
              <p className="text-sm text-blue-800 mb-2">
                Pro users can export a data bundle for grant applications including:
              </p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Profile Summary:</strong> ABN, State, Sector</li>
                <li>• <strong>2026 Emission Totals:</strong> kWh, Litres, and Tonnes (NGA standards)</li>
                <li>• <strong>Evidence Log:</strong> List of uploaded filenames/sizes for audit-readiness</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GrantsPage;
